import { z } from 'zod';
import { daysBetween } from './calendar/dates.js';
import { isoDateSchema } from './projects.js';
import { queryList } from './task-list.js';
import { estimateHoursSchema, taskTypeSchema } from './tasks.js';

/** RES-03, RES-04, RES-08: resource bookings (allocations) and their API schemas. */

export const allocationUnitSchema = z.enum(['hours_per_day', 'percent']);
export type AllocationUnitValue = z.infer<typeof allocationUnitSchema>;
export const bookingTypeSchema = z.enum(['tentative', 'confirmed']);
export type BookingType = z.infer<typeof bookingTypeSchema>;

/** Longest booking; longer engagements are split. */
export const MAX_ALLOCATION_DAYS = 3 * 366;
/** Longest period of one list or heatmap query (docs/05: at most 12 months). */
export const MAX_RESOURCE_QUERY_DAYS = 366;
/** Upper bound of one booking list (like the task list, docs/02). */
export const MAX_ALLOCATION_LIST = 2000;

/** Hours per day: up to 24 in quarter hours; percent of the profile hours: up to 200. */
export function isValidAmount(unit: AllocationUnitValue, amount: number): boolean {
  if (!(amount > 0)) return false;
  return unit === 'hours_per_day'
    ? amount <= 24 && Number.isInteger(amount * 4)
    : amount <= 200 && Number.isInteger(amount * 100);
}

export const allocationNoteSchema = z.string().trim().max(2000);

type Period = { startDate: string; endDate: string };

function checkPeriod(value: Period, ctx: z.RefinementCtx) {
  if (value.endDate < value.startDate) {
    ctx.addIssue({ code: 'custom', message: 'Ende vor Beginn', path: ['endDate'] });
  } else if (daysBetween(value.startDate, value.endDate) >= MAX_ALLOCATION_DAYS) {
    ctx.addIssue({ code: 'custom', message: 'Zeitraum zu lang', path: ['endDate'] });
  }
}

/** Exactly one target: a person or a job role (placeholder, RES-04). */
export const createAllocationBodySchema = z
  .object({
    projectId: z.uuid(),
    taskId: z.uuid().nullable().optional(),
    userId: z.uuid().nullable().optional(),
    jobRoleId: z.uuid().nullable().optional(),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    unit: allocationUnitSchema.default('hours_per_day'),
    amount: z.number(),
    bookingType: bookingTypeSchema.default('tentative'),
    note: allocationNoteSchema.nullable().optional(),
  })
  .superRefine((value, ctx) => {
    checkPeriod(value, ctx);
    const hasUser = (value.userId ?? null) !== null;
    const hasRole = (value.jobRoleId ?? null) !== null;
    if (hasUser === hasRole) {
      ctx.addIssue({
        code: 'custom',
        message: 'Genau eine Person oder eine fachliche Rolle',
        path: ['userId'],
      });
    }
    if (!isValidAmount(value.unit, value.amount)) {
      ctx.addIssue({ code: 'custom', message: 'Ungültiger Umfang', path: ['amount'] });
    }
  });
export type CreateAllocationBody = z.infer<typeof createAllocationBodySchema>;

/**
 * Partial update; the project cannot change. Period, target and amount are checked again by the
 * service after merging with the stored values (`checkAllocationValues`).
 */
export const updateAllocationBodySchema = z.object({
  taskId: z.uuid().nullable().optional(),
  userId: z.uuid().nullable().optional(),
  jobRoleId: z.uuid().nullable().optional(),
  startDate: isoDateSchema.optional(),
  endDate: isoDateSchema.optional(),
  unit: allocationUnitSchema.optional(),
  amount: z.number().optional(),
  bookingType: bookingTypeSchema.optional(),
  note: allocationNoteSchema.nullable().optional(),
});
export type UpdateAllocationBody = z.infer<typeof updateAllocationBodySchema>;

export type AllocationValues = Period & {
  userId: string | null;
  jobRoleId: string | null;
  unit: AllocationUnitValue;
  amount: number;
};

/** Field errors of merged values (period, exactly one target, amount); empty when valid. */
export function checkAllocationValues(
  values: AllocationValues,
): { path: string; message: string }[] {
  const errors: { path: string; message: string }[] = [];
  if (values.endDate < values.startDate)
    errors.push({ path: 'endDate', message: 'Ende vor Beginn' });
  else if (daysBetween(values.startDate, values.endDate) >= MAX_ALLOCATION_DAYS) {
    errors.push({ path: 'endDate', message: 'Zeitraum zu lang' });
  }
  if ((values.userId === null) === (values.jobRoleId === null)) {
    errors.push({ path: 'userId', message: 'Genau eine Person oder eine fachliche Rolle' });
  }
  if (!isValidAmount(values.unit, values.amount)) {
    errors.push({ path: 'amount', message: 'Ungültiger Umfang' });
  }
  return errors;
}

/** POST /allocations/:id/assign – a placeholder becomes a booking of a person (RES-04). */
export const assignAllocationBodySchema = z.object({ userId: z.uuid() });
export type AssignAllocationBody = z.infer<typeof assignAllocationBodySchema>;

export const allocationSchema = z.object({
  id: z.uuid(),
  project: z.object({ id: z.uuid(), key: z.string(), name: z.string() }),
  task: z.object({ id: z.uuid(), key: z.string(), title: z.string() }).nullable(),
  user: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  jobRole: z.object({ id: z.uuid(), name: z.string(), color: z.string() }).nullable(),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  unit: allocationUnitSchema,
  amount: z.number(),
  bookingType: bookingTypeSchema,
  note: z.string().nullable(),
});
export type Allocation = z.infer<typeof allocationSchema>;

/**
 * Consecutive days on which a person is booked beyond their capacity (RES-08). Days without
 * capacity and bookings (weekend, holiday) do not split a span. Only sums, never other projects.
 */
export const overbookingSpanSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  booked: z.number(),
  available: z.number(),
  /** Hours beyond the capacity, summed over the overbooked days. */
  excess: z.number(),
});
export type OverbookingSpan = z.infer<typeof overbookingSpanSchema>;

/** Answer of create, update and assign: saved anyway, with warnings (RES-08). */
export const allocationMutationResponseSchema = z.object({
  allocation: allocationSchema,
  overbooking: z.array(overbookingSpanSchema),
});
export type AllocationMutationResponse = z.infer<typeof allocationMutationResponseSchema>;

function checkRange(value: { from: string; to: string }, ctx: z.RefinementCtx) {
  if (value.to < value.from) {
    ctx.addIssue({ code: 'custom', message: 'Ungültiger Zeitraum', path: ['to'] });
  } else if (daysBetween(value.from, value.to) >= MAX_RESOURCE_QUERY_DAYS) {
    ctx.addIssue({ code: 'custom', message: 'Höchstens 12 Monate', path: ['to'] });
  }
}

/** Bookings overlapping the range, optionally of one project, person or job role. */
export const allocationListQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    projectId: z.uuid().optional(),
    userId: z.uuid().optional(),
    jobRoleId: z.uuid().optional(),
  })
  .superRefine(checkRange);
export type AllocationListQuery = z.infer<typeof allocationListQuerySchema>;

export const allocationListResponseSchema = z.object({
  items: z.array(allocationSchema),
  /** More bookings match than `MAX_ALLOCATION_LIST`; narrow the range or filter. */
  truncated: z.boolean(),
});
export type AllocationListResponse = z.infer<typeof allocationListResponseSchema>;

/** GET /options/job-roles – active job roles for placeholder bookings (internal users). */
export const jobRoleOptionsResponseSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), name: z.string(), color: z.string() })),
});
export type JobRoleOptionsResponse = z.infer<typeof jobRoleOptionsResponseSchema>;

// --- Heatmap queries (RES-05, RES-06, RES-09) -----------------------------------------------------

export const granularitySchema = z.enum(['day', 'week', 'month']);

export const heatmapQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    granularity: granularitySchema.default('week'),
  })
  .superRefine(checkRange);
export type HeatmapQuery = z.infer<typeof heatmapQuerySchema>;

/** GET /resources/roles: optionally only the job roles of one area (RES-15). */
export const roleDemandQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    granularity: granularitySchema.default('week'),
    areaId: z.uuid().optional(),
  })
  .superRefine(checkRange);
export type RoleDemandQuery = z.infer<typeof roleDemandQuerySchema>;

export const utilizationQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    granularity: granularitySchema.default('week'),
    /** People holding this job role (any, not only primary). */
    jobRoleId: z.uuid().optional(),
    /** People with any job role in this area (RES-15). */
    areaId: z.uuid().optional(),
    /** People having all of these skills (RES-16). */
    skillIds: queryList(z.uuid()).optional(),
    /** People with bookings in this project within the range. */
    projectId: z.uuid().optional(),
  })
  .superRefine(checkRange);
export type UtilizationQuery = z.infer<typeof utilizationQuerySchema>;

export const bucketSchema = z.object({
  /** First day of the day/week/month (may lie before `from`). */
  start: isoDateSchema,
  /** Part of the bucket within the queried range. */
  from: isoDateSchema,
  to: isoDateSchema,
});
export type HeatmapBucket = z.infer<typeof bucketSchema>;

const jobRoleRefSchema = z.object({ id: z.uuid(), name: z.string(), color: z.string() });

/** One person × one bucket. Absences and holidays only as counts, never with a reason. */
export const utilizationCellSchema = z.object({
  available: z.number(),
  booked: z.number(),
  /** Part of `booked` from tentative bookings. */
  tentative: z.number(),
  /** Part of `booked` from tasks (RES-11). */
  tasks: z.number(),
  holidayDays: z.number(),
  /** Absent days; half days count 0.5. */
  absenceDays: z.number(),
});
export type UtilizationCell = z.infer<typeof utilizationCellSchema>;

/** GET /resources/utilization – people × buckets (resource.view_all). */
export const utilizationResponseSchema = z.object({
  buckets: z.array(bucketSchema),
  people: z.array(
    z.object({
      user: z.object({ id: z.uuid(), name: z.string() }),
      primaryJobRole: jobRoleRefSchema.nullable(),
      /** Area of the primary job role (grouping, RES-15). */
      area: z.object({ id: z.uuid(), name: z.string() }).nullable(),
      jobRoleIds: z.array(z.uuid()),
      cells: z.array(utilizationCellSchema),
    }),
  ),
});
export type UtilizationResponse = z.infer<typeof utilizationResponseSchema>;

/** One job role × one bucket: demand vs. capacity of the people with this primary role. */
export const roleDemandCellSchema = z.object({
  capacity: z.number(),
  /** Bookings of these people plus placeholders of the role. */
  demand: z.number(),
  /** Part of `demand` from placeholders. */
  placeholder: z.number(),
  /** Part of `demand` from tentative bookings. */
  tentative: z.number(),
  /** Part of `demand` from tasks (RES-11). */
  tasks: z.number(),
});
export type RoleDemandCell = z.infer<typeof roleDemandCellSchema>;

/** GET /resources/roles – job roles × buckets (RES-06, resource.view_all). */
export const roleDemandResponseSchema = z.object({
  buckets: z.array(bucketSchema),
  /** `jobRole` null: people without a primary job role. */
  roles: z.array(
    z.object({
      jobRole: jobRoleRefSchema.nullable(),
      /** Area of the job role (RES-15); null without area or role. */
      area: z.object({ id: z.uuid(), name: z.string() }).nullable(),
      people: z.number().int(),
      cells: z.array(roleDemandCellSchema),
    }),
  ),
});
export type RoleDemandResponse = z.infer<typeof roleDemandResponseSchema>;

/** One person × one bucket in a project; the total only as utilization (no other projects). */
export const projectResourceCellSchema = z.object({
  /** Bookings and task load in this project. */
  booked: z.number(),
  tentative: z.number(),
  /** Part of `booked` from tasks of this project (RES-11). */
  tasks: z.number(),
  /** Total booked ÷ available over all projects in %; null without capacity. */
  totalPercent: z.number().nullable(),
  /** Booked beyond capacity on at least one day of the bucket (also on days off). */
  overbooked: z.boolean(),
});
export type ProjectResourceCell = z.infer<typeof projectResourceCellSchema>;

/** GET /projects/:id/resources – who is booked when and how much in the project (RES-09). */
export const projectResourcesResponseSchema = z.object({
  buckets: z.array(bucketSchema),
  people: z.array(
    z.object({
      user: z.object({ id: z.uuid(), name: z.string() }),
      primaryJobRole: jobRoleRefSchema.nullable(),
      cells: z.array(projectResourceCellSchema),
    }),
  ),
  placeholders: z.array(
    z.object({
      jobRole: jobRoleRefSchema,
      cells: z.array(z.object({ booked: z.number(), tentative: z.number() })),
    }),
  ),
  /** Open tasks with an internal assignee and due date but without estimate (RES-11 hint). */
  tasksWithoutEstimate: z.number().int(),
});
export type ProjectResourcesResponse = z.infer<typeof projectResourcesResponseSchema>;

// --- Load from tasks (RES-11) -----------------------------------------------------------------

/** Tasks behind a heatmap cell: of one person (and optionally one project) in the range. */
export const taskLoadQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    userId: z.uuid().optional(),
    projectId: z.uuid().optional(),
  })
  .superRefine(checkRange);
export type TaskLoadQuery = z.infer<typeof taskLoadQuerySchema>;

export const taskLoadItemSchema = z.object({
  /** null if the viewer may not see the task (only its hours count). */
  task: z.object({ id: z.uuid(), key: z.string(), title: z.string() }).nullable(),
  project: z.object({ id: z.uuid(), key: z.string(), name: z.string() }),
  user: z.object({ id: z.uuid(), name: z.string() }),
  /** Hours of the task for this person within the queried range. */
  hours: z.number(),
  /** Remaining work of the task: estimate × (1 − progress) (E-36). */
  remaining: z.number(),
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema,
});
export type TaskLoadItem = z.infer<typeof taskLoadItemSchema>;

export const taskLoadResponseSchema = z.object({ items: z.array(taskLoadItemSchema) });
export type TaskLoadResponse = z.infer<typeof taskLoadResponseSchema>;

// --- Personal view (RES-12) -------------------------------------------------------------------

const projectRef = z.object({ id: z.uuid(), key: z.string(), name: z.string() });

/** GET /me/resources, GET /resources/people/:id – one person, total and per project. */
export const personResourcesResponseSchema = z.object({
  user: z.object({ id: z.uuid(), name: z.string() }),
  buckets: z.array(bucketSchema),
  total: z.array(utilizationCellSchema),
  /** Projects with bookings or task load of the person in the range, by key. */
  projects: z.array(
    z.object({
      project: projectRef,
      cells: z.array(z.object({ booked: z.number(), tentative: z.number(), tasks: z.number() })),
    }),
  ),
});
export type PersonResourcesResponse = z.infer<typeof personResourcesResponseSchema>;

export const personItemsQuerySchema = z
  .object({ from: isoDateSchema, to: isoDateSchema })
  .superRefine(checkRange);
export type PersonItemsQuery = z.infer<typeof personItemsQuerySchema>;

/** Bookings and tasks of the person behind a cell (RES-12). */
export const personItemsResponseSchema = z.object({
  bookings: z.array(allocationSchema),
  tasks: z.array(taskLoadItemSchema),
});
export type PersonItemsResponse = z.infer<typeof personItemsResponseSchema>;

// --- Overbooking hint for tasks (RES-14, E-35) -------------------------------------------------

/**
 * POST /projects/:projectId/tasks/load-preview – would these values overbook the assignee?
 * Without `taskId` for a new task (type and status from the body); with `taskId` the stored load
 * of that task is replaced by these values (type, status, own booking and subtasks as stored).
 */
export const taskLoadPreviewBodySchema = z.object({
  taskId: z.uuid().optional(),
  /** Parent of a new subtask: with an estimate the parent stops counting (RES-11). */
  parentId: z.uuid().optional(),
  type: taskTypeSchema.optional(),
  statusId: z.uuid().optional(),
  assigneeId: z.uuid().nullable(),
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
  estimateHours: estimateHoursSchema.nullable(),
});
export type TaskLoadPreviewBody = z.infer<typeof taskLoadPreviewBodySchema>;

/**
 * `checked`: the task counts, `overbooking` lists the assignee's overbooked periods (from today
 * on). Otherwise nothing was checked: no internal assignee, no estimate, no due date, or the task does
 * not count (milestone, done, own booking, subtasks with estimate).
 */
export const taskLoadPreviewStatusSchema = z.enum([
  'checked',
  'no_assignee',
  'no_estimate',
  'no_due_date',
  'not_counted',
]);
export type TaskLoadPreviewStatus = z.infer<typeof taskLoadPreviewStatusSchema>;

/** Only names, periods and sums: nothing about other projects (docs/04). */
export const taskLoadPreviewResponseSchema = z.object({
  status: taskLoadPreviewStatusSchema,
  /** The internal assignee, also when not overbooked; null without one. */
  user: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  overbooking: z.array(overbookingSpanSchema),
});
export type TaskLoadPreviewResponse = z.infer<typeof taskLoadPreviewResponseSchema>;
